import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiHelpCircle,
  FiTruck,
  FiPackage,
  FiFileText,
  FiShield,
  FiPhoneCall,
  FiMail,
  FiArrowLeft,
} from "react-icons/fi";

interface FaqItem {
  id: string;
  category: "orders" | "products" | "payments" | "warranty";
  question: string;
  answer: string;
}

const faqData: FaqItem[] = [
  {
    id: "1",
    category: "orders",
    question: "How do I track my order status?",
    answer:
      "You can track your order at any time by clicking on 'Track Order' in the footer or opening your Account/Orders section. We also send real-time SMS and Email updates with tracking links as soon as your shipment is dispatched.",
  },
  {
    id: "2",
    category: "orders",
    question: "What are the estimated delivery timelines for commercial HVAC products?",
    answer:
      "Standard metro orders are delivered within 2-4 business days. For bulk orders, heavy VRV/VRF equipment, or custom copper fittings, delivery usually takes 4-7 business days depending on transit location.",
  },
  {
    id: "3",
    category: "orders",
    question: "Can I modify or cancel my order after placing it?",
    answer:
      "Orders can be cancelled or updated prior to dispatch directly through your Account Orders drawer. Once an order is in transit, please contact our support team immediately to assist with routing modifications.",
  },
  {
    id: "4",
    category: "products",
    question: "Are all HoneyCool products genuine and certified?",
    answer:
      "Yes, 100% of products sold on HoneyCool are sourced directly from authorized manufacturers (e.g. Dunan Metal, DA, Hailiang) and come with manufacturer material test certificates (MTC) on request.",
  },
  {
    id: "5",
    category: "products",
    question: "What is the difference between Single Coil (Roll) and Box pricing?",
    answer:
      "Single Coil pricing applies to individual rolls or unit items. Box pricing provides wholesale volume discounts when purchasing full factory boxes (e.g., 64 Pcs/Box or 10 Rolls/Box).",
  },
  {
    id: "6",
    category: "products",
    question: "How do I verify technical specifications like working pressure and sizing?",
    answer:
      "Each product detail page contains detailed 'Core Specifications' and 'Technical Specifications' tables listing Maximum Working Pressure (MPa), Size (inches/mm), Ambient Temperature tolerance, and alloy specs.",
  },
  {
    id: "7",
    category: "payments",
    question: "Will I get a valid B2B GST tax invoice for business purchases?",
    answer:
      "Absolutely. All prices shown on HoneyCool are inclusive of 18% GST. Enter your company GSTIN during checkout to receive an input tax credit (ITC) compliant GST tax invoice.",
  },
  {
    id: "8",
    category: "payments",
    question: "What payment methods do you accept?",
    answer:
      "We accept all major Credit/Debit Cards, Net Banking, UPI (Google Pay, PhonePe, Paytm), NEFT/RTGS for corporate orders, and Cash on Delivery (COD) for eligible pincodes.",
  },
  {
    id: "9",
    category: "warranty",
    question: "What warranty coverage is provided on HoneyCool valves and fittings?",
    answer:
      "All HoneyCool brass and copper fittings carry a standard 1-year manufacturer defect warranty against pressure leakage and material defects under standard operating conditions.",
  },
  {
    id: "10",
    category: "warranty",
    question: "How do I request a Material Test Certificate (MTC) or compliance doc?",
    answer:
      "For engineering projects requiring MTC documents, simply provide your Order ID to support@honeycool.com or contact your account executive after placing the order.",
  },
];

const categoryTabs = [
  { id: "all", label: "All Questions", icon: FiHelpCircle },
  { id: "orders", label: "Orders & Shipping", icon: FiTruck },
  { id: "products", label: "Products & Specs", icon: FiPackage },
  { id: "payments", label: "Payments & GST", icon: FiFileText },
  { id: "warranty", label: "Warranty & Support", icon: FiShield },
];

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [openIds, setOpenIds] = useState<string[]>(["1", "4", "7"]);

  const toggleAccordion = (id: string) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const filteredFaqs = faqData.filter((faq) => {
    const matchesTab = activeTab === "all" || faq.category === activeTab;
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <div
      style={{
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        paddingBottom: "80px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Header Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #0d9488 0%, #1565c0 100%)",
          color: "#ffffff",
          padding: "40px 20px 70px",
          textAlign: "center",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto", textAlign: "left", marginBottom: "16px" }}>
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.25)",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              backdropFilter: "blur(8px)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.15)")}
          >
            <FiArrowLeft size={18} />
            Back
          </button>
        </div>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <span
            style={{
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              padding: "6px 16px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "700",
              textTransform: "uppercase",
              letterSpacing: "1px",
              display: "inline-block",
              marginBottom: "16px",
            }}
          >
            Help Center
          </span>
          <h1
            style={{
              fontSize: "36px",
              fontWeight: "900",
              margin: "0 0 12px 0",
              letterSpacing: "-0.5px",
            }}
          >
            Frequently Asked Questions
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "rgba(255, 255, 255, 0.85)",
              margin: "0 auto 32px auto",
              maxWidth: "600px",
              lineHeight: "1.6",
            }}
          >
            Have questions about HoneyCool HVAC equipment, pricing, GST invoicing, or shipment tracking? We're here to help.
          </p>

          {/* Search Box */}
          <div
            style={{
              position: "relative",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <FiSearch
              size={20}
              style={{
                position: "absolute",
                left: "18px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
              }}
            />
            <input
              type="text"
              placeholder="Search by keyword, product name, GST, delivery..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "16px 20px 16px 50px",
                borderRadius: "12px",
                border: "none",
                fontSize: "15px",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                outline: "none",
                boxSizing: "border-box",
                color: "#1e293b",
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div
        style={{
          maxWidth: "1000px",
          margin: "-30px auto 0 auto",
          padding: "0 20px",
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Category Tabs */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            overflowX: "auto",
            paddingBottom: "12px",
            marginBottom: "24px",
          }}
        >
          {categoryTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 18px",
                  borderRadius: "30px",
                  border: isActive ? "2px solid #0d9488" : "1px solid #e2e8f0",
                  backgroundColor: isActive ? "#ffffff" : "#ffffff",
                  color: isActive ? "#0d9488" : "#64748b",
                  fontWeight: isActive ? "700" : "500",
                  fontSize: "14px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: isActive
                    ? "0 4px 12px rgba(13, 148, 136, 0.15)"
                    : "0 1px 3px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s ease",
                }}
              >
                <Icon size={16} color={isActive ? "#0d9488" : "#94a3b8"} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* FAQ Accordion List */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => {
              const isOpen = openIds.includes(faq.id);
              return (
                <div
                  key={faq.id}
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <button
                    onClick={() => toggleAccordion(faq.id)}
                    style={{
                      width: "100%",
                      padding: "20px 24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: isOpen ? "#f0fdfa" : "#ffffff",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      gap: "16px",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: isOpen ? "#0f766e" : "#1e293b",
                        lineHeight: "1.4",
                      }}
                    >
                      {faq.question}
                    </span>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        backgroundColor: isOpen ? "#ccfbf1" : "#f1f5f9",
                        color: isOpen ? "#0d9488" : "#64748b",
                        flexShrink: 0,
                      }}
                    >
                      {isOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                    </span>
                  </button>

                  {isOpen && (
                    <div
                      style={{
                        padding: "0 24px 20px 24px",
                        backgroundColor: isOpen ? "#f0fdfa" : "#ffffff",
                        color: "#475569",
                        fontSize: "14.5px",
                        lineHeight: "1.7",
                        borderTop: "1px solid #f1f5f9",
                        paddingTop: "16px",
                      }}
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "48px 24px",
                textAlign: "center",
                color: "#64748b",
                border: "1px solid #e2e8f0",
              }}
            >
              <FiHelpCircle size={40} color="#cbd5e1" style={{ marginBottom: "12px" }} />
              <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px 0" }}>
                No matching questions found
              </h3>
              <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
                Try adjusting your search terms or select a different category filter.
              </p>
            </div>
          )}
        </div>

        {/* Contact Support Footer CTA */}
        <div
          style={{
            marginTop: "48px",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            padding: "36px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "24px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "800",
                color: "#0f172a",
                margin: "0 0 6px 0",
              }}
            >
              Still have questions?
            </h3>
            <p style={{ margin: 0, color: "#64748b", fontSize: "14.5px" }}>
              Our HVAC support engineers are available to assist with your technical inquiries.
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("openOrdersModal"))}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 20px",
                borderRadius: "8px",
                border: "1.5px solid #0d9488",
                backgroundColor: "#ffffff",
                color: "#0d9488",
                fontWeight: "700",
                fontSize: "14px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <FiPackage size={16} />
              View Orders
            </button>
            <Link
              to="/terms-and-conditions"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 20px",
                borderRadius: "8px",
                backgroundColor: "#0d9488",
                color: "#ffffff",
                fontWeight: "700",
                fontSize: "14px",
                textDecoration: "none",
                transition: "all 0.2s ease",
              }}
            >
              <FiPhoneCall size={16} />
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
